import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, ChevronDown, ChevronRight, Copy, RotateCcw, Search, Shield, Upload,
} from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import { useFlash } from '../../context/ToastContext.jsx';
import AnimatedCounter from '../../components/AnimatedCounter.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import Loader from '../../components/Loader.jsx';
import { api, ApiError } from '../../api/client.js';

// /admin/grants — provisioning console for every user_overlays row, rebuilt
// around the content-status classifier (functions/_lib/overlayHtml.js):
//
//   CURRENT — default copy matching today's template     (green)
//   STALE   — default copy from before a template deploy (amber)
//   CUSTOM  — hand-uploaded file; bulk-protected         (blue)
//   PENDING — no R2 copy yet                             (muted)
//   UNKNOWN — pre-metadata object that differs from the current default;
//             admin resolves via "Use default" or "Mark custom"
//
// The everyday deploy-wave flow is one button: "Re-apply stale (N)".
// Everything else is selection-based bulk (custom copies excluded unless
// explicitly overridden). Failures surface per row AND as a summary — and a
// 401/403 anywhere raises a blocking session-expired modal instead of
// silently doing nothing (the 2026-08-18 incident).

const STATUS_META = {
  current: { label: 'Current', chip: 'adm-chip--ok' },
  stale: { label: 'Stale', chip: 'adm-chip--amber' },
  custom: { label: 'Custom', chip: 'adm-chip--blue' },
  pending: { label: 'Pending', chip: 'adm-chip--wait' },
  unknown: { label: 'Unknown', chip: 'adm-chip--bad' },
};
const STATUS_ORDER = ['current', 'stale', 'custom', 'pending', 'unknown'];

const ROW_DIM = {
  current: 'rgba(0,230,118,0.04)',
  stale: 'rgba(255,171,0,0.05)',
  custom: 'rgba(0,212,255,0.05)',
  pending: 'rgba(138,143,166,0.04)',
  unknown: 'rgba(255,45,120,0.05)',
};

const COLS = { '--adm-cols': '34px minmax(260px, 1.4fr) 1fr 190px', '--adm-minw': '844px' };

function ago(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function Grants() {
  const [grants, setGrants] = useState(null);
  const setFlash = useFlash();
  // token → { busy?, ok?, error? } — per-row action feedback, kept across
  // reloads so a bulk run's ticks/errors stay visible next to each row.
  const [rowStates, setRowStates] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [collapsedUsers, setCollapsedUsers] = useState(() => new Set());
  const [sessionExpired, setSessionExpired] = useState(false);
  // { tokens, customCount, label } — pending bulk confirmation.
  const [confirm, setConfirm] = useState(null);
  const [includeCustom, setIncludeCustom] = useState(false);
  const [bulk, setBulk] = useState(null); // { done, total, failed }
  const fileInputs = useRef(new Map());

  function setRow(token, patch) {
    setRowStates((prev) => ({ ...prev, [token]: patch }));
  }

  function authGuard(e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      setSessionExpired(true);
      return true;
    }
    return false;
  }

  function load() {
    api.adminListGrants()
      .then((data) => {
        const next = data?.grants ?? [];
        setGrants(next);
        // Prune selection to tokens that still exist.
        setSelected((prev) => {
          const alive = new Set(next.map((g) => g.accessToken));
          return new Set([...prev].filter((t) => alive.has(t)));
        });
      })
      .catch((e) => {
        if (!authGuard(e)) setFlash({ kind: 'danger', msg: e.message });
        setGrants((prev) => prev ?? []);
      });
  }

  useEffect(() => { load(); }, []);

  // ── derived views ────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { all: grants?.length ?? 0 };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const g of grants ?? []) c[g.contentStatus] = (c[g.contentStatus] ?? 0) + 1;
    return c;
  }, [grants]);

  const filtered = useMemo(() => {
    let list = grants ?? [];
    if (statusFilter !== 'all') list = list.filter((g) => g.contentStatus === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((g) =>
        (g.user?.username ?? '').toLowerCase().includes(q)
        || (g.overlay?.name ?? '').toLowerCase().includes(q)
        || (g.overlay?.slug ?? '').toLowerCase().includes(q)
        || (g.package?.name ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [grants, statusFilter, query]);

  const groups = useMemo(() => {
    const byUser = new Map();
    for (const g of filtered) {
      const key = g.user?.username ?? '—';
      if (!byUser.has(key)) byUser.set(key, []);
      byUser.get(key).push(g);
    }
    for (const list of byUser.values()) {
      list.sort((a, b) =>
        (a.package?.name ?? '').localeCompare(b.package?.name ?? '')
        || (a.overlay?.name ?? '').localeCompare(b.overlay?.name ?? ''));
    }
    return [...byUser.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const staleTokens = useMemo(
    () => (grants ?? []).filter((g) => g.contentStatus === 'stale').map((g) => g.accessToken),
    [grants],
  );

  // ── single-row actions ───────────────────────────────────────────────────
  async function applyDefault(token) {
    setRow(token, { busy: true });
    try {
      await api.adminApplyDefaultGrantHtml(token);
      setRow(token, { ok: true });
      load();
    } catch (e) {
      setRow(token, { error: e.message });
      if (!authGuard(e)) setFlash({ kind: 'danger', msg: `Apply failed: ${e.message}` });
    }
  }

  async function markCustom(token) {
    setRow(token, { busy: true });
    try {
      await api.adminMarkGrantCustom(token);
      setRow(token, { ok: true });
      load();
    } catch (e) {
      setRow(token, { error: e.message });
      if (!authGuard(e)) setFlash({ kind: 'danger', msg: `Mark custom failed: ${e.message}` });
    }
  }

  async function onPick(token, ev) {
    const file = ev.target.files?.[0];
    ev.target.value = ''; // allow re-picking the same filename later
    if (!file) return;
    setRow(token, { busy: true });
    try {
      await api.adminUploadGrantHtml(token, file);
      setRow(token, { ok: true });
      setFlash({ kind: 'success', msg: `Uploaded ${file.name} (${(file.size / 1024).toFixed(1)} KB) — marked CUSTOM.` });
      load();
    } catch (e) {
      setRow(token, { error: e.message });
      if (!authGuard(e)) setFlash({ kind: 'danger', msg: `Upload failed: ${e.message}` });
    }
  }

  function copyToken(token) {
    navigator.clipboard?.writeText(token).then(
      () => setFlash({ kind: 'info', msg: 'Token copied to clipboard.' }),
      () => setFlash({ kind: 'danger', msg: 'Clipboard unavailable.' }),
    );
  }

  // ── bulk ─────────────────────────────────────────────────────────────────
  // Sequential on purpose: N simultaneous template writes would melt the
  // API for zero benefit, and one slot of progress at a time reads clearly.
  async function runBulk(tokens) {
    setBulk({ done: 0, total: tokens.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i];
      setRow(token, { busy: true });
      try {
        await api.adminApplyDefaultGrantHtml(token); // eslint-disable-line no-await-in-loop
        setRow(token, { ok: true });
      } catch (e) {
        failed += 1;
        setRow(token, { error: e.message });
        if (authGuard(e)) {
          // Session died mid-run — stop burning the queue; the modal explains.
          setBulk(null);
          load();
          return;
        }
      }
      setBulk({ done: i + 1, total: tokens.length, failed });
    }
    setBulk(null);
    setFlash(failed === 0
      ? { kind: 'success', msg: `Applied defaults to ${tokens.length} grant${tokens.length === 1 ? '' : 's'}.` }
      : { kind: 'warning', msg: `${tokens.length - failed} applied, ${failed} failed — failures are marked on their rows.` });
    load();
  }

  function requestBulk(tokens, label) {
    const pool = (grants ?? []).filter((g) => tokens.includes(g.accessToken));
    setIncludeCustom(false);
    setConfirm({
      tokens,
      label,
      customCount: pool.filter((g) => g.contentStatus === 'custom').length,
    });
  }

  function confirmBulk() {
    const pool = (grants ?? []).filter((g) => confirm.tokens.includes(g.accessToken));
    const targets = pool
      .filter((g) => includeCustom || g.contentStatus !== 'custom')
      .map((g) => g.accessToken);
    setConfirm(null);
    if (targets.length) runBulk(targets);
    else setFlash({ kind: 'info', msg: 'Nothing to apply — all selected grants are custom uploads.' });
  }

  function toggleSelected(token, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(token); else next.delete(token);
      return next;
    });
  }

  function toggleGroup(list, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const g of list) {
        if (on) next.add(g.accessToken); else next.delete(g.accessToken);
      }
      return next;
    });
  }

  function toggleCollapsed(username) {
    setCollapsedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username); else next.add(username);
      return next;
    });
  }

  const busyAny = bulk !== null;

  return (
    <AdminShell crumb="Grants">
      <header className="adm-head adm-head--split adm-rv" style={{ animationDelay: '80ms' }}>
        <div>
          <h1 className="adm-head__title">Grants</h1>
          <p className="adm-head__sub">
            Every per-overlay grant, with the freshness of its served copy.
            {' '}<code>/o/&lt;token&gt;</code> serves exactly what each row says.
          </p>
        </div>
        <div className="adm-stats">
          <div className="adm-stat adm-stat--ok">
            <div className="adm-stat__value"><AnimatedCounter to={counts.current ?? 0} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Current</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--amber">
            <div className="adm-stat__value"><AnimatedCounter to={counts.stale ?? 0} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Stale</div>
          </div>
          <span className="adm-stats__sep" aria-hidden="true" />
          <div className="adm-stat adm-stat--blue">
            <div className="adm-stat__value"><AnimatedCounter to={counts.custom ?? 0} pad={2} duration={1200} /></div>
            <div className="adm-stat__label">Custom</div>
          </div>
        </div>
      </header>


      {grants == null ? (
        <Loader label="Loading grants…" />
      ) : (
        <>
          {/* ── filters ── */}
          <div className="adm-filterbar adm-rv" style={{ animationDelay: '140ms' }}>
            <div className="adm-search" style={{ width: 360 }}>
              <Search size={16} aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search user, overlay, package…"
                aria-label="Search grants"
              />
            </div>
            <div className="adm-pills" role="group" aria-label="Filter by content status">
              {['all', ...STATUS_ORDER].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`adm-pill${statusFilter === s ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                  aria-pressed={statusFilter === s}
                >
                  {s === 'all' ? 'All' : STATUS_META[s].label}
                  {' '}
                  <span className="adm-pill__count">{counts[s] ?? 0}</span>
                </button>
              ))}
            </div>
            <span className="adm-shown">{filtered.length} of {counts.all} shown</span>
            <button
              type="button"
              className="adm-act adm-act--amber"
              disabled={busyAny || staleTokens.length === 0}
              onClick={() => requestBulk(staleTokens, `Re-apply the current default to ${staleTokens.length} stale grant${staleTokens.length === 1 ? '' : 's'}?`)}
              title="Re-apply the current default template to every STALE grant copy"
            >
              <RotateCcw size={13} aria-hidden="true" />
              {`Re-apply stale (${staleTokens.length})`}
            </button>
          </div>

          {/* ── selection / bulk-progress bar ── */}
          {(selected.size > 0 || bulk) && (
            <div className="adm-selbar">
              {bulk ? (
                <span className="adm-selbar__count">
                  Applying… {bulk.done}/{bulk.total}
                  {bulk.failed > 0 ? ` (${bulk.failed} failed)` : ''}
                </span>
              ) : (
                <>
                  <span className="adm-selbar__count">{selected.size} selected</span>
                  <button
                    type="button"
                    className="adm-act"
                    onClick={() => requestBulk([...selected], `Apply the current default to ${selected.size} selected grant${selected.size === 1 ? '' : 's'}?`)}
                  >
                    Apply default to selected
                  </button>
                  <button
                    type="button"
                    className="adm-act adm-act--quiet"
                    onClick={() => setSelected(new Set())}
                  >
                    Clear
                  </button>
                  <span className="adm-selbar__note">
                    Custom uploads are skipped — they stay protected.
                  </span>
                </>
              )}
            </div>
          )}

          <div className="adm-scroll adm-scroll--cards adm-scroll--check adm-rv" style={{ ...COLS, animationDelay: '220ms' }}>
            <div className="adm-grid__rule" aria-hidden="true" style={{ marginTop: 18 }} />

            {/* ── per-user groups ── */}
            {groups.map(([username, list], gi) => {
              const collapsed = collapsedUsers.has(username);
              const allSelected = list.every((g) => selected.has(g.accessToken));
              const nStale = list.filter((g) => g.contentStatus === 'stale').length;
              const nCustom = list.filter((g) => g.contentStatus === 'custom').length;
              const nUnknown = list.filter((g) => g.contentStatus === 'unknown').length;
              let summary = `${list.length} grant${list.length === 1 ? '' : 's'}`;
              if (nStale) summary += ` · ${nStale} stale`;
              if (nUnknown) summary += ` · ${nUnknown} unknown`;
              if (nCustom) summary += ` · ${nCustom} custom`;
              return (
                <section
                  key={username}
                  className="adm-rvl"
                  style={{ animationDelay: `${90 + gi * 70}ms` }}
                  aria-label={`Grants for ${username}`}
                >
                  <div className="adm-group__head">
                    <button
                      type="button"
                      className="adm-group__toggle"
                      onClick={() => toggleCollapsed(username)}
                      title={collapsed ? 'Expand' : 'Collapse'}
                      aria-label={`${collapsed ? 'Expand' : 'Collapse'} grants for ${username}`}
                      aria-expanded={!collapsed}
                    >
                      {collapsed
                        ? <ChevronRight size={15} aria-hidden="true" />
                        : <ChevronDown size={15} aria-hidden="true" />}
                    </button>
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      checked={allSelected && list.length > 0}
                      onChange={(e) => toggleGroup(list, e.target.checked)}
                      aria-label={`Select all grants for ${username}`}
                    />
                    <span className="adm-initial" aria-hidden="true">
                      {username[0]?.toUpperCase() ?? '?'}
                    </span>
                    <span className="adm-group__name">{username}</span>
                    <span className="adm-group__summary">{summary}</span>
                  </div>
                  {!collapsed && list.map((g) => {
                    const rs = rowStates[g.accessToken] ?? {};
                    const status = g.contentStatus ?? (g.htmlR2Key ? 'unknown' : 'pending');
                    const meta = STATUS_META[status] ?? STATUS_META.unknown;
                    return (
                      <div
                        className="adm-grid__row"
                        key={g.accessToken}
                        style={{ minHeight: 60, '--row-dim': ROW_DIM[status] ?? ROW_DIM.pending }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={selected.has(g.accessToken)}
                          onChange={(e) => toggleSelected(g.accessToken, e.target.checked)}
                          aria-label={`Select ${g.overlay?.name}`}
                        />
                        <div style={{ paddingLeft: 4, minWidth: 0 }}>
                          <span className="adm-ident__name" style={{ fontSize: 16 }}>
                            {g.overlay?.name ?? g.overlay?.slug ?? '—'}
                          </span>
                          <span className="adm-cell__sub">
                            {g.package?.name ?? g.package?.slug ?? '—'}
                          </span>
                        </div>
                        {/* display:contents on desktop; the card's chip line
                            under the name on phones (same pattern as Users). */}
                        <div className="adm-grid__meta">
                          <div>
                            <span className={`adm-chip ${meta.chip}`} title={g.note ?? undefined}>
                              {meta.label}
                            </span>
                            <span className="adm-cell__sub">
                              {rs.busy ? 'Applying…' : (
                                <>
                                  {g.appliedAt && `applied ${ago(g.appliedAt)}`}
                                  {g.appliedBy && ` by ${g.appliedBy}`}
                                  {g.sourceFilename && status === 'custom' && ` · ${g.sourceFilename}`}
                                </>
                              )}
                            </span>
                            {rs.error && <span className="adm-cell__err">{rs.error}</span>}
                          </div>
                        </div>
                        <div className="adm-grid__actions">
                          <input
                            ref={(el) => {
                              if (el) fileInputs.current.set(g.accessToken, el);
                              else fileInputs.current.delete(g.accessToken);
                            }}
                            type="file"
                            className="d-none"
                            accept=".html,text/html,application/xhtml+xml"
                            onChange={(ev) => onPick(g.accessToken, ev)}
                          />
                          {rs.ok && !rs.busy && (
                            <span className="adm-chip adm-chip--ok" aria-label="Done" style={{ gap: 0 }}>
                              <Check size={14} aria-hidden="true" />
                            </span>
                          )}
                          {status === 'unknown' && g.htmlR2Key && (
                            <button
                              type="button"
                              className="btn-icon"
                              disabled={rs.busy || busyAny}
                              onClick={() => markCustom(g.accessToken)}
                              title="This file is a hand-customised copy — mark it CUSTOM so bulk re-applies never overwrite it"
                              aria-label="Mark as custom"
                            >
                              <Shield size={16} aria-hidden="true" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => copyToken(g.accessToken)}
                            title={`Copy access token (${g.accessToken.slice(0, 12)}…)`}
                            aria-label="Copy access token"
                          >
                            <Copy size={16} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-icon--warning"
                            disabled={rs.busy || busyAny}
                            onClick={() => applyDefault(g.accessToken)}
                            title={`Use ${g.templatePath ?? `/overlay/${g.overlay?.slug}.html`} as the per-user copy${g.htmlR2Key ? ' (overwrites existing)' : ''}`}
                            aria-label="Use default template"
                          >
                            <RotateCcw size={16} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-icon--primary"
                            disabled={rs.busy || busyAny}
                            onClick={() => fileInputs.current.get(g.accessToken)?.click()}
                            title={g.htmlR2Key ? 'Re-upload HTML (marks CUSTOM)' : 'Upload HTML (marks CUSTOM)'}
                            aria-label="Upload HTML"
                          >
                            <Upload size={16} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </section>
              );
            })}

            {groups.length === 0 && (
              <div className="adm-empty">
                {counts.all === 0 ? 'No grants in the system yet.' : 'No grants match the current filter.'}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── bulk confirm ── */}
      <ConfirmModal
        open={confirm != null}
        title="Apply default templates?"
        body={confirm && (
          <>
            <p className="mb-2">{confirm.label}</p>
            <p className="text-muted small mb-2">
              Each grant gets the current <code>/overlay/&lt;slug&gt;.html</code>,
              overwriting its existing copy.
            </p>
            {confirm.customCount > 0 && (
              <div className="form-check">
                <input
                  id="grants-include-custom"
                  type="checkbox"
                  className="form-check-input"
                  checked={includeCustom}
                  onChange={(e) => setIncludeCustom(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="grants-include-custom" style={{ color: 'var(--warning)' }}>
                  Also overwrite {confirm.customCount} CUSTOM upload{confirm.customCount === 1 ? '' : 's'} (hand-built files — normally protected)
                </label>
              </div>
            )}
          </>
        )}
        confirmLabel="Apply"
        confirmKind={includeCustom ? 'danger' : 'primary'}
        onConfirm={confirmBulk}
        onClose={() => setConfirm(null)}
      />

      {/* ── session expired ── */}
      <ConfirmModal
        open={sessionExpired}
        title="Session expired"
        body={(
          <p className="mb-0">
            Your admin login has expired, so nothing is being saved — this is
            why clicks would otherwise appear to do nothing. Log in again to
            continue.
          </p>
        )}
        confirmLabel="Go to login"
        confirmKind="primary"
        onConfirm={() => window.location.assign('/login')}
        onClose={() => setSessionExpired(false)}
      />
    </AdminShell>
  );
}
